import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { Helmet } from 'react-helmet-async'

interface ContactSettings {
    contact_address?: string
    contact_phone?: string
    contact_email?: string
    contact_hours?: string
    social_facebook?: string
    social_instagram?: string
    social_twitter?: string
    social_youtube?: string
    social_tiktok?: string
}

interface PageData {
    title_en: string
    title_vi: string
    content_en: string
    content_vi: string
    meta_description_en: string
    meta_description_vi: string
}

export default function ContactPage() {
    useTranslation()
    const { language } = useLanguageStore()

    const [page, setPage] = useState<PageData | null>(null)
    const [settings, setSettings] = useState<ContactSettings>({})
    const [loading, setLoading] = useState(true)
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    })
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                // Fetch page content and settings in parallel
                const [pageRes, settingsRes] = await Promise.all([
                    fetch('/api/pages/contact'),
                    fetch('/api/settings/public')
                ])

                const pageData = await pageRes.json()
                const settingsData = await settingsRes.json()

                if (pageData.success && pageData.data) {
                    setPage(pageData.data)
                }
                if (settingsData.success && settingsData.data) {
                    // Convert array to object
                    const settingsObj: ContactSettings = {}
                    settingsData.data.forEach((s: { key: string, value: string }) => {
                        settingsObj[s.key as keyof ContactSettings] = s.value
                    })
                    setSettings(settingsObj)
                }
            } catch (err) {
                console.error('Error fetching contact data:', err)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setSending(true)

        // Simulate sending (in production, this would call an API)
        await new Promise(resolve => setTimeout(resolve, 1500))

        setSending(false)
        setSent(true)
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' })

        // Reset success message after 5 seconds
        setTimeout(() => setSent(false), 5000)
    }

    const title = page ? (language === 'en' ? page.title_en : (page.title_vi || page.title_en)) : (language === 'en' ? 'Contact Us' : 'Liên Hệ')
    const content = page ? (language === 'en' ? page.content_en : (page.content_vi || page.content_en)) : ''
    const metaDescription = page ? (language === 'en' ? page.meta_description_en : (page.meta_description_vi || page.meta_description_en)) : ''

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-pulse text-center">
                    <div className="w-16 h-16 bg-primary-200 rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-500">{language === 'en' ? 'Loading...' : 'Đang tải...'}</p>
                </div>
            </div>
        )
    }

    return (
        <>
            <Helmet>
                <title>{title} | Phú Quốc Rental</title>
                <meta name="description" content={metaDescription || 'Contact Phú Quốc Rental for vehicle rentals and tours'} />
                <link rel="canonical" href="https://phuquoc-rental.pages.dev/contact" />
            </Helmet>

            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
                <div className="container mx-auto px-4">
                    {/* Breadcrumb */}
                    <div className="mb-6">
                        <Link
                            to="/"
                            className="inline-flex items-center text-gray-600 hover:text-primary-600 transition"
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            {language === 'en' ? 'Back to Home' : 'Về trang chủ'}
                        </Link>
                    </div>

                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-3xl md:text-4xl font-bold mb-4">{title}</h1>
                        {content && (
                            <div
                                className="max-w-2xl mx-auto text-gray-600 prose prose-p:text-gray-600"
                                dangerouslySetInnerHTML={{ __html: content }}
                            />
                        )}
                    </div>

                    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Contact Info */}
                        <div className="space-y-8">
                            <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8">
                                <h2 className="text-xl font-bold mb-6">
                                    {language === 'en' ? 'Contact Information' : 'Thông Tin Liên Hệ'}
                                </h2>

                                <div className="space-y-6">
                                    {settings.contact_address && (
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <MapPin className="w-6 h-6 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold mb-1">{language === 'en' ? 'Address' : 'Địa chỉ'}</h3>
                                                <p className="text-gray-600">{settings.contact_address}</p>
                                            </div>
                                        </div>
                                    )}

                                    {settings.contact_phone && (
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <Phone className="w-6 h-6 text-green-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold mb-1">{language === 'en' ? 'Phone' : 'Điện thoại'}</h3>
                                                <a href={`tel:${settings.contact_phone}`} className="text-gray-600 hover:text-primary-600">
                                                    {settings.contact_phone}
                                                </a>
                                            </div>
                                        </div>
                                    )}

                                    {settings.contact_email && (
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <Mail className="w-6 h-6 text-blue-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold mb-1">Email</h3>
                                                <a href={`mailto:${settings.contact_email}`} className="text-gray-600 hover:text-primary-600">
                                                    {settings.contact_email}
                                                </a>
                                            </div>
                                        </div>
                                    )}

                                    {settings.contact_hours && (
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <Clock className="w-6 h-6 text-orange-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold mb-1">{language === 'en' ? 'Business Hours' : 'Giờ làm việc'}</h3>
                                                <p className="text-gray-600">{settings.contact_hours}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Social Links */}
                                {(settings.social_facebook || settings.social_instagram) && (
                                    <div className="mt-8 pt-6 border-t">
                                        <h3 className="font-semibold mb-4">{language === 'en' ? 'Follow Us' : 'Theo dõi chúng tôi'}</h3>
                                        <div className="flex gap-4">
                                            {settings.social_facebook && (
                                                <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer"
                                                    className="w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center hover:bg-blue-700 transition">
                                                    <span className="text-lg">f</span>
                                                </a>
                                            )}
                                            {settings.social_instagram && (
                                                <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer"
                                                    className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-500 text-white rounded-lg flex items-center justify-center hover:opacity-90 transition">
                                                    <span className="text-lg">📷</span>
                                                </a>
                                            )}
                                            {settings.social_youtube && (
                                                <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer"
                                                    className="w-10 h-10 bg-red-600 text-white rounded-lg flex items-center justify-center hover:bg-red-700 transition">
                                                    <span className="text-lg">▶️</span>
                                                </a>
                                            )}
                                            {settings.social_tiktok && (
                                                <a href={settings.social_tiktok} target="_blank" rel="noopener noreferrer"
                                                    className="w-10 h-10 bg-black text-white rounded-lg flex items-center justify-center hover:bg-gray-800 transition">
                                                    <span className="text-lg">♪</span>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Map placeholder */}
                            <div className="bg-gray-200 rounded-2xl h-64 flex items-center justify-center">
                                <p className="text-gray-500">{language === 'en' ? 'Map coming soon' : 'Bản đồ sắp có'}</p>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8">
                            <h2 className="text-xl font-bold mb-6">
                                {language === 'en' ? 'Send us a Message' : 'Gửi Tin Nhắn'}
                            </h2>

                            {sent && (
                                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-700">
                                    <CheckCircle className="w-5 h-5" />
                                    {language === 'en' ? 'Message sent successfully!' : 'Gửi tin nhắn thành công!'}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            {language === 'en' ? 'Your Name' : 'Họ tên'} *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            placeholder={language === 'en' ? 'John Doe' : 'Nguyễn Văn A'}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Email *</label>
                                        <input
                                            type="email"
                                            required
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            placeholder="email@example.com"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            {language === 'en' ? 'Phone' : 'Số điện thoại'}
                                        </label>
                                        <input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            placeholder="+84 123 456 789"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            {language === 'en' ? 'Subject' : 'Chủ đề'} *
                                        </label>
                                        <select
                                            required
                                            value={formData.subject}
                                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        >
                                            <option value="">{language === 'en' ? 'Select a topic' : 'Chọn chủ đề'}</option>
                                            <option value="booking">{language === 'en' ? 'Booking Inquiry' : 'Hỏi đặt xe'}</option>
                                            <option value="support">{language === 'en' ? 'Customer Support' : 'Hỗ trợ khách hàng'}</option>
                                            <option value="owner">{language === 'en' ? 'Become an Owner' : 'Trở thành chủ xe'}</option>
                                            <option value="partnership">{language === 'en' ? 'Partnership' : 'Hợp tác'}</option>
                                            <option value="other">{language === 'en' ? 'Other' : 'Khác'}</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Message' : 'Nội dung'} *
                                    </label>
                                    <textarea
                                        required
                                        rows={5}
                                        value={formData.message}
                                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                                        placeholder={language === 'en' ? 'How can we help you?' : 'Chúng tôi có thể giúp gì cho bạn?'}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50"
                                >
                                    {sending ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            {language === 'en' ? 'Sending...' : 'Đang gửi...'}
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-5 h-5" />
                                            {language === 'en' ? 'Send Message' : 'Gửi tin nhắn'}
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
